import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial state
const initialState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  error: null
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Dynamic API base URL - Use IP when accessing from mobile
const getApiBaseUrl = () => {
  const currentHost = window.location.hostname;

  // Production: API is on the same domain
  if (currentHost === 'wandervibe.app' || currentHost.includes('herokuapp.com')) {
    return '/api';
  }

  // Local development with mobile testing
  if (currentHost === '192.168.0.209') {
    return 'http://192.168.0.209:5005/api';
  }

  // Local development
  return 'http://localhost:5005/api';
};

const API_BASE_URL = getApiBaseUrl();
console.log('🔧 AUTH_CONTEXT: Current hostname:', window.location.hostname);
console.log('🔧 AUTH_CONTEXT: Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('🔧 AUTH_CONTEXT: Using API_BASE_URL:', API_BASE_URL);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Helper function to make API calls
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('🌐 API_CALL: Making request to URL:', url);
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(state.token && { Authorization: `Bearer ${state.token}` })
        },
        ...options
      };

      console.log('🌐 API_CALL: Request config:', { url, method: config.method || 'GET' });
      const response = await fetch(url, config);
      console.log('🌐 API_CALL: Response status:', response.status);
      const data = await response.json();
      console.log('🌐 API_CALL: Response data:', data);

      if (!response.ok) {
        const error = new Error(data.message || 'Something went wrong');
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      // Handle network errors or server not running
      if (error.message.includes('fetch') || error.name === 'TypeError') {
        throw new Error('Unable to connect to server. Please check if the server is running.');
      }
      throw error;
    }
  };

  // Login function
  const login = async (credentials) => {
    console.log('🔐 LOGIN: Starting login process with credentials:', { ...credentials, password: '***' });
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      console.log('🔐 LOGIN: Making API call to /auth/login');
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });
      console.log('🔐 LOGIN: API call response:', { ...data, token: data.token ? '***' : undefined });

      if (data.success) {
        console.log('🔐 LOGIN: Login successful, storing token and user data');
        // Store token in localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: data.user,
            token: data.token
          }
        });

        console.log('🔐 LOGIN: Login process completed successfully');
        return data;
      } else {
        console.error('🔐 LOGIN: Login failed with message:', data.message);
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('🔐 LOGIN: Login error occurred:', error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Signup function
  const signup = async (userData) => {
    console.log('🚀 SIGNUP: Starting signup process with data:', { ...userData, password: '***', confirmPassword: '***' });
    console.log('🚀 SIGNUP: Using API_BASE_URL:', API_BASE_URL);
    
    try {
      console.log('🚀 SIGNUP: Setting loading state...');
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      console.log('🚀 SIGNUP: Making API call to:', `${API_BASE_URL}/auth/signup`);
      const data = await apiCall('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      console.log('🚀 SIGNUP: API call completed, data:', data);

      if (data.success) {
        console.log('🚀 SIGNUP: Success! Storing auth data...');
        // Store token in localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        console.log('🚀 SIGNUP: Dispatching login success...');
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: data.user,
            token: data.token
          }
        });

        console.log('🚀 SIGNUP: Signup completed successfully!');
        return data;
      } else {
        console.error('🚀 SIGNUP: API returned success=false');
        throw new Error(data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('🚀 SIGNUP: Error caught:', error);
      console.error('🚀 SIGNUP: Error message:', error.message);
      console.error('🚀 SIGNUP: Error status:', error.status);
      console.error('🚀 SIGNUP: Error data:', error.data);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint if user is authenticated
      if (state.isAuthenticated) {
        await apiCall('/auth/logout', {
          method: 'POST'
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Get current user profile
  const getCurrentUser = async () => {
    try {
      const data = await apiCall('/auth/me');
      
      if (data.success) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: data.user
        });
        
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        
        return data.user;
      }
    } catch (error) {
      console.error('Get current user error:', error);
      // If token is invalid, logout
      await logout();
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    console.log('👤 PROFILE_UPDATE: Starting profile update with data:', updates);
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      console.log('👤 PROFILE_UPDATE: Making API call to /auth/profile');
      const data = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      console.log('👤 PROFILE_UPDATE: API response received:', data);

      if (data.success) {
        console.log('👤 PROFILE_UPDATE: Success! Updating user in context');
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: data.user
        });
        
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        
        console.log('👤 PROFILE_UPDATE: Profile update completed successfully');
        return data.user;
      } else {
        console.error('👤 PROFILE_UPDATE: API returned success=false:', data);
        throw new Error(data.message || 'Profile update failed');
      }
    } catch (error) {
      console.error('👤 PROFILE_UPDATE: Error occurred:', error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Change password
  const changePassword = async (passwords) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const data = await apiCall('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(passwords)
      });

      if (data.success) {
        return data;
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const data = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      return data;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Reset password
  const resetPassword = async (token, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const data = await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password })
      });

      return data;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Verify email
  const verifyEmail = async (token) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const data = await apiCall('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token })
      });

      if (data.success) {
        // Update user verification status
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: { isVerified: true }
        });
        
        const updatedUser = { ...state.user, isVerified: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      return data;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');

        if (token && userData) {
          const user = JSON.parse(userData);
          
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user,
              token
            }
          });

          // Try to verify token is still valid and get updated user data
          try {
            await getCurrentUser();
          } catch (serverError) {
            // If server is not available, still proceed with cached user data
            console.warn('Server not available, using cached user data:', serverError.message);
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Token refresh functionality
  useEffect(() => {
    let refreshTimer;

    if (state.token && state.isAuthenticated) {
      // Refresh token every 30 minutes
      refreshTimer = setInterval(async () => {
        try {
          await getCurrentUser();
        } catch (error) {
          console.warn('Token refresh error:', error.message);
          // Only logout if the error is not a network/server issue
          if (!error.message.includes('Unable to connect to server')) {
            await logout();
          }
        }
      }, 30 * 60 * 1000); // 30 minutes
    }

    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [state.token, state.isAuthenticated]);

  const value = {
    ...state,
    login,
    signup,
    logout,
    getCurrentUser,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    verifyEmail,
    clearError,
    apiCall
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;